import React, { useState, useEffect, ChangeEvent } from "react";
import { Modal, Button, Spinner, Form, Image } from "react-bootstrap";
import { API_BASE_URL } from "../../../config";
import { useDataFetch } from "../../../hooks/use-data-fetch";

interface Order {
  id: string;
  payment_proof?: string | null;
}

interface PaymentProofModalProps {
  isOpen: boolean;
  toggle: () => void;
  order: Order | null;
}

export const PaymentProofModal: React.FC<PaymentProofModalProps> = ({
  isOpen,
  toggle,
  order,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [proofUrl, setProofUrl] = useState<string | null>(null);

  const { postData } = useDataFetch();

  useEffect(() => {
    if (order?.payment_proof) {
      setProofUrl(`${API_BASE_URL}/order/payment-proof/${order.payment_proof}`);
    } else {
      setProofUrl(null);
    }
  }, [order]);

  const { mutateAsync: uploadProof } = postData(
    "/order/upload/payment-proof",
    {},
    {
      onSuccess: (response: { fileName?: string }) => {
        if (response?.fileName) {
          setProofUrl(`${API_BASE_URL}/order/payment-proof/${response.fileName}`);
        }
        setIsUploading(false);
      },
      onError: (err: unknown) => {
        console.error("❌ Error subiendo comprobante:", err);
        setIsUploading(false);
      },
    }
  );

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !order) return;

    setIsUploading(true);

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result as string;
      try {
        const payload = {
          orderId: order.id,
          file: {
            name: file.name,
            base64URL: base64String,
          },
        };

        await uploadProof(payload);
      } catch (err) {
        console.error("❌ Upload failed:", err);
        setIsUploading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  if (!order) return null;

  return (
    <Modal show={isOpen} onHide={toggle} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Comprobante de pago</Modal.Title>
      </Modal.Header>

      <Modal.Body className="text-center">
        {proofUrl ? (
          <>
            <Image
              src={proofUrl}
              alt="Comprobante de pago"
              fluid
              rounded
              className="mb-3"
            />
            <div className="d-flex justify-content-center gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                href={proofUrl}
                target="_blank"
              >
                Ver / Descargar
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setProofUrl(null)}
              >
                Subir otro
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-3">No se encontró comprobante para esta orden.</div>

            <Form.Group controlId="paymentProofUpload">
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={isUploading}
              />
            </Form.Group>

            {isUploading && (
              <div className="mt-3">
                <Spinner animation="border" size="sm" /> Subiendo comprobante...
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};